import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import Button from '../../components/ui/Button';
import PasswordInput from '../../components/PasswordInput';
import '../../App.css';
import { translateError } from '../../utils/errorMessages';

interface IMembership {
  organization: {
    _id: string;
    name: string;
    slug?: string;
  } | string;
  isActive: boolean;
}

interface IUser {
  _id: string;
  name: string;
  email: string;
  phoneNumber?: string;
  memberships: IMembership[];
  isSuperAdmin?: boolean;
  lastLogin?: string;
  createdAt: string;
}

interface IOrganization {
  _id: string;
  name: string;
  slug: string;
}

const UserDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<IUser | null>(null);
  const [organizations, setOrganizations] = useState<IOrganization[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [password, setPassword] = useState('');
  const [memberships, setMemberships] = useState<Array<{
    organization: string;
    isActive: boolean;
  }>>([]);

  // New membership form state
  const [newOrgId, setNewOrgId] = useState('');

  const fetchData = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const [userResponse, orgsResponse] = await Promise.all([
        api.superAdmin.users.get(id),
        api.superAdmin.organizations.list(),
      ]);

      const userData = userResponse.data;
      setUser(userData);
      setOrganizations(orgsResponse.data.organizations || []);

      // Populate form
      setName(userData.name);
      setEmail(userData.email);
      setPhoneNumber(userData.phoneNumber || '');
      setIsSuperAdmin(userData.isSuperAdmin || false);

      // Extract memberships
      const userMemberships = (userData.memberships || []).map((m: IMembership) => ({
        organization: typeof m.organization === 'object' ? m.organization._id : String(m.organization),
        isActive: m.isActive,
      }));
      setMemberships(userMemberships);

      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
      if (err.response?.status === 404) {
        navigate('/super-admin/users');
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addMembership = () => {
    if (!newOrgId) {
      setError(t('superAdmin.users.selectOrganization'));
      return;
    }

    // Check if membership for this org already exists
    if (memberships.some(m => m.organization === newOrgId)) {
      setError(t('superAdmin.users.membershipExists'));
      return;
    }

    setMemberships([...memberships, {
      organization: newOrgId,
      isActive: true,
    }]);
    setNewOrgId('');
    setError(null);
  };

  const removeMembership = (index: number) => {
    setMemberships(memberships.filter((_, i) => i !== index));
  };

  const toggleMembershipActive = (index: number) => {
    setMemberships(memberships.map((m, i) =>
      i === index ? { ...m, isActive: !m.isActive } : m
    ));
  };

  const getOrgName = (orgId: string): string => {
    const org = organizations.find(o => o._id === orgId);
    return org?.name || orgId;
  };


  const submitHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Auto-add pending membership if org is selected
      let finalMemberships = [...memberships];
      if (newOrgId) {
        if (!finalMemberships.some(m => m.organization === newOrgId)) {
          finalMemberships.push({
            organization: newOrgId,
            isActive: true,
          });
        }
      }

      const updateData: any = {
        name,
        email,
        phoneNumber,
        isSuperAdmin,
        memberships: finalMemberships.map(m => ({
          organization: m.organization,
          isActive: m.isActive,
        })),
      };

      if (password) {
        updateData.password = password;
      }

      await api.superAdmin.users.update(id!, updateData);
      navigate('/super-admin/users');
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>{t('common.loading')}</p>;
  if (error && !user) return <p className="error-message">{t('common.error')}: {error}</p>;
  if (!user) return <p>{t('superAdmin.users.notFound')}</p>;

  // Filter out organizations that already have memberships for the "add" dropdown
  const availableOrgs = organizations.filter(
    org => !memberships.some(m => m.organization === org._id)
  );

  return (
    <div className="admin-form-page">
      <h2>{t('superAdmin.users.editTitle')}</h2>

      {error && <p className="error-message">{t('common.error')}: {error}</p>}

      <form onSubmit={submitHandler} className="form-container">
        <div className="form-group">
          <label htmlFor="name">{t('common.name')}</label>
          <input
            type="text"
            id="name"
            placeholder={t('superAdmin.users.enterName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="email">{t('common.email')}</label>
          <input
            type="email"
            id="email"
            placeholder={t('superAdmin.users.enterEmail')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="phoneNumber">{t('common.phoneNumber')}</label>
          <input
            type="tel"
            id="phoneNumber"
            placeholder={t('superAdmin.users.enterPhoneNumber')}
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="password">{t('common.password')}</label>
          <PasswordInput
            id="password"
            placeholder={t('superAdmin.users.leavePasswordBlank')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div className="form-group checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={isSuperAdmin}
              onChange={(e) => setIsSuperAdmin(e.target.checked)}
            />
            {t('superAdmin.users.isSuperAdmin')}
          </label>
          <small className="form-help">{t('superAdmin.users.superAdminWarning')}</small>
        </div>

        {/* Memberships Section */}
        <div className="form-group">
          <label>{t('superAdmin.users.memberships')}</label>

          {memberships.length === 0 ? (
            <p className="form-help">{t('superAdmin.users.noMemberships')}</p>
          ) : (
            <div className="memberships-list">
              {memberships.map((membership, index) => (
                <div key={index} className="membership-item">
                  <div className="membership-org">
                    <strong>{getOrgName(membership.organization)}</strong>
                  </div>
                  <div className="membership-status">
                    <label>
                      <input
                        type="checkbox"
                        checked={membership.isActive}
                        onChange={() => toggleMembershipActive(index)}
                      />
                      {t('superAdmin.users.active')}
                    </label>
                  </div>
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => removeMembership(index)}
                    title={t('common.remove')}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add new membership */}
          {availableOrgs.length > 0 && (
            <div className="add-membership">
              <select
                value={newOrgId}
                onChange={(e) => setNewOrgId(e.target.value)}
              >
                <option value="">{t('superAdmin.users.selectOrganization')}</option>
                {availableOrgs.map(org => (
                  <option key={org._id} value={org._id}>{org.name}</option>
                ))}
              </select>
              <button
                type="button"
                className="btn-add"
                onClick={addMembership}
              >
                {t('common.add')}
              </button>
            </div>
          )}
        </div>

        <div className="info-section">
          <div className="info-row">
            <span className="info-label">{t('superAdmin.users.lastLogin')}:</span>
            <span>{user.lastLogin ? new Date(user.lastLogin).toLocaleString() : '-'}</span>
          </div>
          <div className="info-row">
            <span className="info-label">{t('superAdmin.users.createdAt')}:</span>
            <span>{new Date(user.createdAt).toLocaleString()}</span>
          </div>
        </div>

        <Button type="submit" fullWidth loading={saving}>
          {saving ? t('common.saving') : t('common.save')}
        </Button>
      </form>

      <Link to="/super-admin/users" className="form-link">
        {t('superAdmin.users.goBack')}
      </Link>
    </div>
  );
};

export default UserDetailPage;
